import { Transition } from '@headlessui/react'
import { CodeBracketSquareIcon, SparklesIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { connect, FieldArray, FormikValues } from 'formik'
import { get, isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'
import { v4 } from 'uuid'
import { addMethod, array, boolean, object, string } from 'yup'

import ListBox from '@/components/GenericForm/ListBox'
import Categories, { Category } from '@/components/Segments/Builder/Categories'
import Button from '@/components/TailwindUI/Button'
import TextArea from '@/components/TailwindUI/TextArea'
import TextInput from '@/components/TailwindUI/TextInput'
import Toggle from '@/components/TailwindUI/Toggle'
import useFeatures from '@/components/useFeatures'
import { APPBUILDER_FEATURE_CATEGORIES } from '@/constants/featureToggles'
import CodeEditor from '@/pages/SpaceAppBuilder/components/CodeEditor/CodeEditor'

import messages from './messages'

const StyledSection = styled.div`
  margin-bottom: 20px;
`

const StyledSectionDescription = styled.div`
  margin: 2px 0;
  font-size: 0.85rem;
`

const getValidationSchema = (intl: IntlShape, formObject: FormikValues, isDevelopmentFeature: boolean) => {
  addMethod(array, 'unique', function (message, mapper = (a: any) => a) {
    // @ts-expect-error
    return this.test('unique', message, list => {
      return list?.length > 0 ? list?.length === new Set(list?.map(mapper)).size : true
    })
  })
  return {
    hasChatDiscoverability: boolean(),
    chatDiscoverabilityPhrase: string().when('hasChatDiscoverability', {
      is: hasChatDiscoverability => hasChatDiscoverability === true,
      then: string().required(intl.formatMessage(messages.pleaseDefineChatDiscoverabilityPhrase)),
      otherwise: string()
    }),
    payload: object()
      .typeError(({ originalValue }) => {
        try {
          JSON.parse(originalValue)
        } catch {
          return intl.formatMessage(messages.payloadInvalidJson)
        }
        return true
      })
      .required(intl.formatMessage(messages.pleaseDefinePayload)),
    parameters: array()
      .of(
        object().shape({
          name: string().required('Name is required'),
          allowedValues: string(),
          type: string().required('Type is required').min(1, 'Type is required'),
          description: string().when(`hasChatDiscoverability`, {
            is: () => formObject.hasChatDiscoverability === true && isDevelopmentFeature,
            then: string().required('Description is required.'),
            otherwise: string()
          }),
          example: string().when(`hasChatDiscoverability`, {
            is: () => !formObject.hasChatDiscoverability,
            then: string().required('Example Value for parameter is required'),
            otherwise: string()
          })
        })
      )
      // @ts-expect-error
      .unique('Please provide unique parameter names.', a => a.name)
  }
}

const getInitialValues = (triggerData: any) => {
  return {
    payload: get(triggerData, 'payload', '{}'),
    parameters: get(triggerData, 'parameters'),
    hasChatDiscoverability: triggerData?.hasChatDiscoverability || false,
    chatDiscoverabilityPhrase: triggerData?.chatDiscoverabilityPhrase,
    categories: get(triggerData, 'categories') || []
  }
}
type Parameter = {
  id?: string
  name: string
  description: string
  allowedValues: string
  hasAllowedValues: boolean
  required: boolean
  example?: string
  type: string
}
type AppUiTriggerProps = {
  readOnly?: boolean
}

const AppUiTrigger = ({ formik, readOnly = false }: AppUiTriggerProps & FormikValues) => {
  const intl = useIntl()

  const { values, setFieldValue, errors, touched, setFieldError } = formik

  const { [APPBUILDER_FEATURE_CATEGORIES]: featureCategories } = useFeatures()
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [typeOptions, setTypeOptions] = useState<{ label: string; value: string | number }[]>([])

  useEffect(() => {
    let typeOptions = [
      { label: 'Select a type', value: '' },
      { label: 'String', value: 'string' },
      { label: 'Number', value: 'number' },
      { label: 'Boolean', value: 'boolean' }
    ]
    if (values.triggerData.hasChatDiscoverability) {
      // reset type to valid type options when chat discoverable
      const parameters = values.triggerData.parameters?.map((param: Parameter) => ({
        ...param,
        type: typeOptions.some(option => option.value === param.type) ? param.type : ''
      }))
      setFieldValue('triggerData.parameters', parameters)
    } else {
      typeOptions = typeOptions.concat([
        { label: 'Object', value: 'object' },
        { label: 'Array', value: 'array' }
      ])
    }
    setTypeOptions(typeOptions)

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [values.triggerData.hasChatDiscoverability])

  useEffect(() => {
    const params = values.triggerData.parameters

    const convertToType = (type: string, example: string) => {
      try {
        switch (type) {
          case 'number': {
            return Number(example)
          }
          case 'boolean': {
            return Boolean(example)
          }
          case 'object': {
            return JSON.parse(example)
          }
          case 'array': {
            return JSON.parse(example)
          }
          default: {
            return example?.toString()
          }
        }
      } catch {
        return example
      }
    }

    if (values.triggerData.parameters) {
      const payload = params.reduce((acc: any, param: { name: any; example: any; type?: string | undefined }) => {
        const { name, example, type = 'string' } = param
        return { ...acc, [name]: convertToType(type, example) }
      }, {})
      setFieldValue('triggerData.payload', payload)
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [values.triggerData.parameters])

  useEffect(() => {
    // use timeout to delay race condition with sync between params and payload editor
    const timeoutId = setTimeout(() => {
      const parameters = values.triggerData.parameters || []
      let payload: any
      try {
        payload =
          typeof values.triggerData.payload === 'string'
            ? JSON.parse(values.triggerData.payload)
            : values.triggerData.payload
      } catch {}

      const verifyInputType = (value: any) => {
        if (Array.isArray(value)) {
          return { type: 'array', value: JSON.stringify(value) }
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return { type: 'object', value: JSON.stringify(value) }
        } else {
          return {
            type: 'string',
            value
          }
        }
      }

      if (payload && !isEmpty(payload)) {
        for (const key in payload) {
          const paramIndex = parameters?.findIndex((param: any) => param.name === key)
          if (paramIndex >= 0) {
            parameters[paramIndex].example = verifyInputType(payload[key]).value
          } else {
            parameters.push({
              id: v4(),
              name: key,
              description: '',
              type: verifyInputType(payload[key]).type,
              allowedValues: '',
              hasAllowedValues: false,
              example: verifyInputType(payload[key]).value,
              required: false
            })
          }
        }
        setFieldValue(
          'triggerData.parameters',
          parameters.filter((param: any) => Object.keys(payload).includes(param.name))
        )
      }
    }, 500)

    return () => clearTimeout(timeoutId)
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [values.triggerData.payload])

  return (
    <>
      <StyledSection>
        <h3 className="font-semibold">{intl.formatMessage(messages.chatDiscoverability)}</h3>
        <div className="mt-1 mr-2">
          <Toggle
            label="Make this UI discoverable via chat conversations"
            disabled={readOnly}
            checked={values.triggerData.hasChatDiscoverability}
            onChange={() => {
              formik.setFieldValue('triggerData.hasChatDiscoverability', !values.triggerData.hasChatDiscoverability)
            }}
          />
        </div>
        <AnimateHeight duration={200} height={values.triggerData.hasChatDiscoverability ? 'auto' : 0}>
          <div className="mt-5">
            <StyledSectionDescription>
              {intl.formatMessage(messages.chatDiscoverabilityPhraseMessage)}
            </StyledSectionDescription>
            <TextArea
              disabled={readOnly}
              name="triggerData.chatDiscoverabilityPhrase"
              value={get(values, `triggerData.chatDiscoverabilityPhrase`)}
              required={true}
              onChange={e => setFieldValue('triggerData.chatDiscoverabilityPhrase', e.target.value)}
              placeholderIcon={<SparklesIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />}
              error={
                touched?.triggerData?.chatDiscoverabilityPhrase && errors?.triggerData?.chatDiscoverabilityPhrase
                  ? errors?.triggerData?.chatDiscoverabilityPhrase?.toString()
                  : undefined
              }
              placeholder={intl.formatMessage(messages.chatDiscoverabilityPhrasePlaceholder)}
            />
          </div>
        </AnimateHeight>
      </StyledSection>
      {featureCategories && (
        <Categories
          name="triggerData.categories"
          value={values.triggerData.categories}
          heading={"Which topic(s) best categorize this feature's content?"}
          subHeading={'This will help the conversational model get users to the right information.'}
          className="mt-2"
          disabled={readOnly}
          onAdd={category => {
            let categories: Array<Category> = [...get(values, 'triggerData.categories', [])]
            categories = [...categories, category]
            formik.setFieldValue('triggerData.categories', categories)
          }}
          onRemove={i => {
            const categories: Array<Category> = [...get(values, 'triggerData.categories', [])]
            categories.splice(i, 1)
            formik.setFieldValue('triggerData.categories', categories)
          }}
        />
      )}
      <div className="mt-6 flex justify-between">
        <h3 className="font-semibold text-zinc-900">
          {values.triggerData.hasChatDiscoverability ? 'UI Parameters' : 'UI Payload'}
        </h3>
        {!values.triggerData.hasChatDiscoverability && (
          <Button color="transparent" onClick={() => setShowAdvanced(prevState => !prevState)}>
            {showAdvanced ? (
              <XMarkIcon className="inline-flex h-5 w-5 text-zinc-500" />
            ) : (
              <CodeBracketSquareIcon className="inline-flex h-5 w-5 text-zinc-500" />
            )}
            <span className="zinc-500 ml-2 text-xs">{showAdvanced ? 'Hide Advanced' : 'Advanced'}</span>
          </Button>
        )}
      </div>
      {!showAdvanced && (
        <FieldArray
          name={`triggerData.parameters`}
          render={arrayHelpers => (
            <>
              {values.triggerData.parameters?.map((param: Parameter, index: number) => (
                <Transition
                  appear={true}
                  key={param.id}
                  show={true}
                  enter="transition-all duration-300"
                  enterFrom="opacity-0 h-0"
                  enterTo="opacity-100 h-auto"
                  leave="transition-opacity duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="mt-3 grid grid-cols-1 items-center gap-x-6 gap-y-2 border-l-4 border-zinc-300 px-3 pb-3 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                      <p className="text-sm font-normal leading-tight text-zinc-500"> Parameter {index + 1}</p>
                    </div>
                    <div className="ml-auto sm:col-span-3">
                      <Button color="transparent" disabled={readOnly} onClick={() => arrayHelpers.remove(index)}>
                        <TrashIcon className="inline-flex h-4 w-4 text-zinc-500" />
                      </Button>
                    </div>
                    <div className="sm:col-span-2 sm:col-start-1">
                      <TextInput
                        name="name"
                        label="Name"
                        disabled={readOnly}
                        required={true}
                        placeholder="Name"
                        value={param.name}
                        onChange={e =>
                          setFieldValue(`triggerData.parameters[${index}].name`, e.target.value.replaceAll(/\s/g, ''))
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ListBox
                        options={typeOptions}
                        disabled={readOnly}
                        label="Type"
                        required={true}
                        name="type"
                        value={typeOptions.some(option => option.value === param.type) ? param.type : ''}
                        onChange={({ value }) => setFieldValue(`triggerData.parameters[${index}].type`, value)}
                        includeTransition={false}
                      />
                    </div>
                    {values.triggerData.hasChatDiscoverability && (
                      <div className="pt-5 sm:col-span-2">
                        <Toggle
                          onChange={() => setFieldValue(`triggerData.parameters[${index}].required`, !param.required)}
                          checked={param.required}
                          disabled={readOnly}
                          label="Required"
                        />
                      </div>
                    )}
                    {!values.triggerData.hasChatDiscoverability && (
                      <div className="col-span-full">
                        <TextInput
                          name="example"
                          label="Example Value"
                          placeholder="example value"
                          disabled={readOnly}
                          required={true}
                          value={param.example}
                          onChange={e => setFieldValue(`triggerData.parameters[${index}].example`, e.target.value)}
                        />
                      </div>
                    )}

                    {values.triggerData.hasChatDiscoverability && (
                      <>
                        <div className="col-span-full">
                          <TextArea
                            name="description"
                            label="Description"
                            disabled={readOnly}
                            placeholderIcon={<SparklesIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />}
                            placeholder="Enter a description of the Parameter"
                            required={true}
                            value={param.description}
                            onChange={e =>
                              setFieldValue(`triggerData.parameters[${index}].description`, e.target.value)
                            }
                          />
                        </div>
                        <div className="col-span-full">
                          <div className="divide-y divide-gray-200">
                            <div className="relative flex items-start pb-0 pt-3.5">
                              <div className="min-w-0 text-sm leading-6">
                                <label htmlFor="comments" className="font-medium text-gray-900">
                                  Restrict parameter to a list of allowed values?
                                </label>
                              </div>
                              <div className="ml-3 mt-1 flex h-4 items-center">
                                <input
                                  name="hasAllowedValues"
                                  type="checkbox"
                                  disabled={readOnly}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                  checked={param.hasAllowedValues}
                                  onChange={() =>
                                    setFieldValue(
                                      `triggerData.parameters[${index}].hasAllowedValues`,
                                      param.hasAllowedValues === undefined ? true : !param.hasAllowedValues
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-span-full">
                          <AnimateHeight
                            duration={200}
                            height={values.triggerData?.parameters?.[index]?.hasAllowedValues ? 'auto' : 0}
                          >
                            <TextInput
                              name="allowedValues"
                              disabled={readOnly}
                              placeholder="Comma seperated list of allowed values"
                              value={param.allowedValues}
                              onChange={e =>
                                setFieldValue(`triggerData.parameters[${index}].allowedValues`, e.target.value)
                              }
                            />
                          </AnimateHeight>
                        </div>
                      </>
                    )}
                  </div>
                </Transition>
              ))}
              <button
                type="button"
                disabled={readOnly}
                className="block px-3 py-1.5 text-center text-sm font-semibold leading-6 text-blue-600 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                onClick={() =>
                  arrayHelpers.push({
                    id: v4(),
                    name: '',
                    description: '',
                    type: 'string',
                    allowedValues: '',
                    example: '',
                    required: false
                  })
                }
              >
                + Add a parameter
              </button>
            </>
          )}
        />
      )}
      <Transition
        show={showAdvanced}
        enter="transition duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition-all duration-0 transform"
        leaveFrom="translate-x-0 opacity-0 h-0"
        leaveTo="translate-x-full opacity-100 h-auto"
        as="div"
      >
        {showAdvanced && (
          <CodeEditor
            readOnly={readOnly}
            setFieldValue={setFieldValue}
            setFieldError={setFieldError}
            defaultValue={
              typeof values?.triggerData?.payload === 'string'
                ? values.triggerData.payload
                : JSON.stringify(values.triggerData.payload)
            }
            errors={errors}
          />
        )}
      </Transition>
    </>
  )
}

export default connect(AppUiTrigger)
export { getInitialValues, getValidationSchema }
